import React from "react";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Drawer,
  Form,
  Input,
  InputNumber,
  Row,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import {
  EyeOutlined,
  GiftOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { AdminMenuLayout } from "../../../shared/components";
import { getService } from "../../../clients/feathers.clients";
import { walletMovementsService } from "../../../shared/services";
import { decimal } from "../../../shared/utils/Helper";

const { Title, Text } = Typography;

const balanceService = getService("wallet-movements/user/balance");

type WalletBalanceRow = {
  id: number;
  user_id: number;
  name: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  role?: string;
  balance: number;
  movements_count: number;
};

type WalletMovementRow = {
  id: number;
  type?: string;
  amount_net: number;
  description?: string;
  expired_day?: string | null;
  expired_status?: string | null;
  createdAt?: string;
};

export const WalletBalance = () => {
  const [loading, setLoading] = React.useState(false);
  const [rows, setRows] = React.useState<WalletBalanceRow[]>([]);
  const [total, setTotal] = React.useState(0);
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(20);
  const [search, setSearch] = React.useState("");
  const [searchInput, setSearchInput] = React.useState("");

  const [selected, setSelected] = React.useState<WalletBalanceRow | null>(null);
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [movements, setMovements] = React.useState<WalletMovementRow[]>([]);
  const [movementsLoading, setMovementsLoading] = React.useState(false);
  const [creditLoading, setCreditLoading] = React.useState(false);
  const [creditForm] = Form.useForm();

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const res = await balanceService.find({
        query: {
          all: "true",
          $limit: pageSize,
          $skip: (page - 1) * pageSize,
          ...(search ? { search } : {}),
        },
      });
      const data = "data" in res ? res.data : [];
      setRows(Array.isArray(data) ? data : []);
      setTotal("total" in res ? Number(res.total) || 0 : data.length);
    } catch (error: any) {
      message.error(error?.message || "No se pudieron cargar las billeteras");
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search]);

  const loadMovements = React.useCallback(async (userId: number) => {
    setMovementsLoading(true);
    try {
      const res = await walletMovementsService.find({
        query: {
          user_id: userId,
          $limit: 200,
          $sort: { id: -1 },
        },
      });
      const data = "data" in res ? res.data : res;
      setMovements(Array.isArray(data) ? data : []);
    } catch (error: any) {
      message.error(error?.message || "No se pudieron cargar los movimientos");
      setMovements([]);
    } finally {
      setMovementsLoading(false);
    }
  }, []);

  const openDetails = (row: WalletBalanceRow) => {
    setSelected(row);
    setDrawerOpen(true);
    creditForm.resetFields();
    loadMovements(row.user_id);
  };

  const refreshSelectedBalance = async (userId: number) => {
    await load();
    await loadMovements(userId);
    try {
      const res = await balanceService.find({
        query: {
          all: "true",
          $limit: 1,
          $skip: 0,
          search: String(userId),
        },
      });
      const data = ("data" in res ? res.data : []) as WalletBalanceRow[];
      const match = Array.isArray(data)
        ? data.find((item) => Number(item.user_id) === Number(userId))
        : null;
      if (match) setSelected(match);
    } catch {
      // ignore
    }
  };

  const givePromotionalCredit = async (values: {
    amount_net: number;
    description: string;
    expired_day?: dayjs.Dayjs | null;
  }) => {
    if (!selected?.user_id) return;
    setCreditLoading(true);
    try {
      await walletMovementsService.create({
        user_id: selected.user_id,
        type: "admin",
        amount_net: Number(values.amount_net),
        description: values.description?.trim() || "Saldo promocional",
        ...(values.expired_day
          ? {
              expired_day: values.expired_day.endOf("day").format("YYYY-MM-DD HH:mm:ss"),
            }
          : {}),
      });
      message.success("Saldo promocional cargado");
      creditForm.resetFields();
      await refreshSelectedBalance(selected.user_id);
    } catch (error: any) {
      message.error(error?.message || "No se pudo cargar el saldo");
    } finally {
      setCreditLoading(false);
    }
  };

  React.useEffect(() => {
    load();
  }, [load]);

  React.useEffect(() => {
    const t = setTimeout(() => {
      setPage(1);
      setSearch(searchInput.trim());
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  return (
    <AdminMenuLayout pageDescription="Balance de billeteras" title="Balance">
      <Card
        bordered={false}
        style={{
          borderRadius: 14,
          boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
        }}
      >
        <Row
          gutter={[16, 16]}
          align="middle"
          justify="space-between"
          style={{ marginBottom: 16 }}
        >
          <Col>
            <Title level={4} style={{ margin: 0 }}>
              Billeteras
            </Title>
            <Text type="secondary">
              Haz clic en una fila para ver movimientos y cargar saldo
              promocional.
            </Text>
          </Col>
          <Col>
            <Input
              allowClear
              prefix={<SearchOutlined />}
              placeholder="Buscar por nombre, email, teléfono o ID"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              style={{ width: 320, maxWidth: "100%" }}
            />
          </Col>
        </Row>

        <Table
          rowKey="user_id"
          loading={loading}
          dataSource={rows}
          onRow={(row) => ({
            onClick: () => openDetails(row),
            style: { cursor: "pointer" },
          })}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            onChange: (nextPage, nextSize) => {
              setPage(nextPage);
              setPageSize(nextSize);
            },
          }}
          columns={[
            {
              title: "ID",
              dataIndex: "user_id",
              width: 90,
            },
            {
              title: "Usuario",
              dataIndex: "name",
              render: (name: string, row) => (
                <div>
                  <div style={{ fontWeight: 600 }}>
                    {name || `Usuario #${row.user_id}`}
                  </div>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {row.email || "Sin correo"}
                  </Text>
                </div>
              ),
            },
            {
              title: "Teléfono",
              dataIndex: "phone",
              render: (phone: string) => phone || "—",
            },
            {
              title: "Rol",
              dataIndex: "role",
              render: (role: string) => (
                <Tag color={role === "user" ? "blue" : "default"}>
                  {role || "—"}
                </Tag>
              ),
            },
            {
              title: "Movimientos",
              dataIndex: "movements_count",
              width: 120,
            },
            {
              title: "Balance",
              dataIndex: "balance",
              width: 140,
              render: (balance: number) => (
                <Tag
                  color={
                    balance > 0 ? "green" : balance < 0 ? "red" : "default"
                  }
                >
                  $ {decimal(balance)}
                </Tag>
              ),
            },
            {
              title: "",
              key: "actions",
              width: 110,
              render: (_: unknown, row) => (
                <Button
                  type="link"
                  icon={<EyeOutlined />}
                  onClick={(e) => {
                    e.stopPropagation();
                    openDetails(row);
                  }}
                >
                  Ver
                </Button>
              ),
            },
          ]}
        />
      </Card>

      <Drawer
        title={
          selected
            ? `${selected.name || `Usuario #${selected.user_id}`}`
            : "Detalle de billetera"
        }
        width={560}
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setSelected(null);
          setMovements([]);
        }}
        destroyOnClose
      >
        {selected && (
          <Space direction="vertical" size={20} style={{ width: "100%" }}>
            <Card size="small" bordered={false} style={{ background: "#fff4f0" }}>
              <Row gutter={[12, 12]}>
                <Col span={24}>
                  <Text type="secondary">Usuario</Text>
                  <div style={{ fontWeight: 600, fontSize: 16 }}>
                    {selected.name || `Usuario #${selected.user_id}`}
                  </div>
                  <Text type="secondary">{selected.email || "Sin correo"}</Text>
                </Col>
                <Col span={12}>
                  <Text type="secondary">Teléfono</Text>
                  <div>{selected.phone || "—"}</div>
                </Col>
                <Col span={12}>
                  <Text type="secondary">Balance disponible</Text>
                  <div style={{ fontWeight: 700, color: "#E95735", fontSize: 18 }}>
                    $ {decimal(selected.balance)}
                  </div>
                </Col>
              </Row>
            </Card>

            <Card
              size="small"
              title={
                <Space>
                  <GiftOutlined style={{ color: "#E95735" }} />
                  Cargar saldo promocional
                </Space>
              }
            >
              <Form
                form={creditForm}
                layout="vertical"
                onFinish={givePromotionalCredit}
                initialValues={{ amount_net: 10000 }}
              >
                <Form.Item
                  label="Monto"
                  name="amount_net"
                  rules={[{ required: true, message: "Ingresa el monto" }]}
                >
                  <InputNumber
                    min={1}
                    style={{ width: "100%" }}
                    formatter={(value) =>
                      `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                    }
                    parser={(value) =>
                      Number(String(value || "").replace(/\$\s?|(,*)/g, ""))
                    }
                  />
                </Form.Item>
                <Form.Item
                  label="Descripción"
                  name="description"
                  rules={[{ required: true, message: "Describe el motivo" }]}
                  initialValue="Saldo promocional"
                >
                  <Input.TextArea
                    rows={2}
                    placeholder="Ej. Compensación / promo / bono"
                  />
                </Form.Item>
                <Form.Item
                  label="Vence (opcional)"
                  name="expired_day"
                  extra="Si no pones fecha, el saldo no expira"
                >
                  <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
                </Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={creditLoading}
                  block
                >
                  Cargar saldo
                </Button>
              </Form>
            </Card>

            <div>
              <Title level={5} style={{ marginBottom: 12 }}>
                Transacciones
              </Title>
              <Table
                rowKey="id"
                size="small"
                loading={movementsLoading}
                dataSource={movements}
                pagination={{ pageSize: 8 }}
                columns={[
                  {
                    title: "ID",
                    dataIndex: "id",
                    width: 70,
                  },
                  {
                    title: "Monto",
                    dataIndex: "amount_net",
                    render: (amount: number) => (
                      <Tag color={amount >= 0 ? "green" : "red"}>
                        $ {decimal(amount)}
                      </Tag>
                    ),
                  },
                  {
                    title: "Descripción",
                    dataIndex: "description",
                    ellipsis: true,
                  },
                  {
                    title: "Estado",
                    dataIndex: "expired_status",
                    width: 100,
                    render: (status: string | null) =>
                      status ? (
                        <Tag>{status}</Tag>
                      ) : (
                        <Tag color="blue">activo</Tag>
                      ),
                  },
                  {
                    title: "Fecha",
                    dataIndex: "createdAt",
                    width: 110,
                    render: (value: string) =>
                      value ? dayjs(value).format("DD/MM/YY") : "—",
                  },
                ]}
              />
            </div>
          </Space>
        )}
      </Drawer>
    </AdminMenuLayout>
  );
};
